/**
 * Client-side guards for links that originate from scraped event listings.
 *
 * The API re-bases relative links onto their source site, but responses can be
 * cached from before that fix, so the UI checks again before rendering. A link
 * we cannot resolve is dropped rather than shown: a "Register" button that
 * quietly returns the visitor to our own home page is worse than no button.
 */

const SAFE_PROTOCOLS = ['http:', 'https:'];

function parseSafe(value: string, base?: string): string | null {
  try {
    const url = base ? new URL(value, base) : new URL(value);
    return SAFE_PROTOCOLS.includes(url.protocol) ? url.href : null;
  } catch {
    return null;
  }
}

/** Absolute http(s) form of `url`, or null when it cannot be resolved safely. */
export function toAbsoluteUrl(
  url: string | null | undefined,
  bases: Array<string | { source?: string } | null | undefined> = [],
): string | null {
  if (typeof url !== 'string') return null;
  const trimmed = url.trim();
  if (!trimmed) return null;

  const absolute = parseSafe(trimmed);
  if (absolute) return absolute;

  if (/^www\./i.test(trimmed)) return parseSafe(`https://${trimmed}`);
  if (/^[a-z][a-z0-9+.-]*:/i.test(trimmed)) return null;

  for (const candidate of bases) {
    const base = typeof candidate === 'string' ? candidate : candidate?.source;
    if (!base || !base.trim()) continue;
    const safeBase = parseSafe(base.trim());
    if (!safeBase) continue;
    const resolved = parseSafe(trimmed, safeBase);
    if (resolved) return resolved;
  }
  return null;
}

/**
 * Visitor-facing label for an event source link. Uses the host, because the
 * stored `SourceInfo` is internal crawler provenance ("Validated WebIQ event
 * listing (6 events, 95% confidence)") and must never surface publicly.
 */
export function getSourceLabel(source: string | null | undefined): string {
  const absolute = toAbsoluteUrl(source);
  if (!absolute) return 'Event Source';
  try {
    return new URL(absolute).hostname.replace(/^www\./i, '') || 'Event Source';
  } catch {
    return 'Event Source';
  }
}
